import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { CONFIRMATION_MODE, CONFIRMATION_RESULT, WIZ_PHYSIC_POSITION, VALUE_ENTER, VALUE_ESCAPE } from '@wizeoo/akm-core-lib';
import {
  WizBasePopUp,
  WizDatasource,
  FilterComponent,
  WizPopUp,
  ConfirmationResultObj,
  WizRoot,
  WizEventManager, WizEvent
} from '@wizeoo/akm-component-lib';
import { WidgetConfigurationObj } from '@wizeoo/akm-message-lib';
import { EM_ACTION } from '@wizeoo/akm-compute-lib';

import { ViewData } from '../../../model/view.data';
import { MultiDisplayConfig } from '../../multi-display/config/multi-display.config';
import { ScenarioVO } from '../../../vo/scenarii/scenario.vo';
import { MultiDisplay } from '../../multi-display/multi-display.component';
import { ALK_EVENT_TYPE, WIDGET_KIND } from '../../../model/enums';
import { AlkEvent } from '../../../framework/events/alk.event';
import { Model } from '../../../model/model';
import { ScenarioStatsCmd } from '../../../command/scenarii/scenario-stats.cmd';
import { DeleteScenariiCommand } from '../../../command/scenarii/delete-scenarii.cmd';
import { GridsterWidget } from '../../../vo/workingzone/gridster-widget';

import { ScenPopRenderer } from './scen-pop.renderer';
import { ScenAddPop, addScenarioParam } from './scenadd-pop';
import { ScenPopActionRenderer } from './scen-pop.action.renderer';

/** configuration d'entrée pour les popUps */
export interface WidgetScenarioPopDatas {
  model: Model;
  config: WidgetConfigurationObj;
  gridsterConfig: GridsterWidget;
  scenarioCode: string;
}

/**
 * Pop de sélection du périmètre de données du widget (Vision, vision step, step perimeter)
 */
@Component({
  selector: 'widget-scenario-pop',
  templateUrl: './widget-scenario.pop.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetScenarioPop extends WizBasePopUp implements OnDestroy {
  
  // validation sur enter, sortie sur Esc
  @HostListener('window:keydown', ['$event'])
  keyEvent(event: KeyboardEvent) {
    if (event.key === VALUE_ENTER && this.validable) {
      this.save();
    }
    if (event.key === VALUE_ESCAPE) {
      this.hide();
    }
  }

  // dimensions demandées
  public static width: string = '700px';
  public static height: string = '400px';

  private _model : Model = null;
  public get model(): Model {
    return this._model;
  }

  public widgetConfig: WidgetConfigurationObj  = null;
  public scenarioCode: string = null;


  @ViewChild('multiDisp', { read: MultiDisplay })
  multiDisp: MultiDisplay<ScenarioVO>;

  public scenDS: WizDatasource<ScenarioVO> = new WizDatasource<ScenarioVO>([]) // DS de la liste
    .setSortFields([{ property: 'isMain', way: 'asc' }, { property: 'trsLabel', way: 'asc' }])
    .setSelectionMode('single')
    .setFilter(this.myAndGlobalFilterFunc)
    .build();

  /**
   * Constructeur
   * @param elementRef vue phy
   */
  constructor(protected cdref: ChangeDetectorRef) {
    super();
  }

  private myAndGlobalFilterFunc(scen: ScenarioVO): boolean {
    return !scen.userMode || scen.owner ===  this.model.currentUser?.mail;
  }


 /**
   * configuration de l'affichage en multi liste
   */
 public multiDisplayConfig: MultiDisplayConfig<ScenarioVO> = {
  model:  this.model,
  datasource: this.scenDS,
  defaultMode: 'grid',
  onSelectionChanged: this.selectScen.bind(this),
  grid: {
    enabled: true,
    columns: [
      { headerName: '$.common.code.$', width: 100, sortable: true, resizable: true, 
      field: 'code', tooltipField: 'code', cellRenderer: 'scenPopRenderer', },  
      { headerName: ' ', width: 80, sortable: false, resizable: false, 
      field: 'code', tooltipField: 'code', cellRenderer: 'scenAction', },  

      { headerName: '$.common.label.$', flex: 1, sortable: true,resizable: true, field: 'trsLabel', tooltipField: 'trsLabel' },   
      { headerName: '$.common.parent.code.$', width: 100, sortable: true,resizable: true, field: 'parentCode', tooltipField: 'parentCode' },   
      { headerName: '$.common.parent.label.$', flex: 1, sortable: true,resizable: true, field: 'parentTrsLabel', tooltipField: 'parentTrsLabel' },  
    ],
    renderers: {
      scenPopRenderer: ScenPopRenderer, scenAction: ScenPopActionRenderer
    },
  },
  filter: {
    enabled: true,
    initFilter: this.initFilter.bind(this),
  },
};

private mainFilter: FilterComponent;

private initFilter(pFilter: FilterComponent): FilterComponent {
  this.mainFilter = pFilter;
  return this.mainFilter;
}

  public show(): void {
    super.show();
    let params: WidgetScenarioPopDatas = this.datas;
    this._model = params.model;
    this.widgetConfig = params.config;
    this.scenarioCode = params.scenarioCode;
    this.feed(); // alimentation des steps (1x)
    this.setListeners();
  }

  /** pose des écouteurs  */
  private setListeners(): void {
    // Listener sur l'evenement ALK_EVENT_ADD emis lors de la demande de création d'un scen
    WizEventManager.addListener(this, this.multiDisp, ALK_EVENT_TYPE.ADD, this.addScen);
    // Listener sur l'evenement ALK_EVENT_DELETE emis lors de la demande de suppression d'un scen
    WizEventManager.addListener(this, this.multiDisp, ALK_EVENT_TYPE.DELETE, this.askdeleteScen);
    // Listener sur l'evenement ALK_EVENT_DELETE emis lors de la demande d'édition d'un scen
    WizEventManager.addListener(this, this.multiDisp, ALK_EVENT_TYPE.EDIT, this.editScen);
  }

  ngOnDestroy() {
    WizEventManager.removeListener(this, this.multiDisp, ALK_EVENT_TYPE.ADD);
    WizEventManager.removeListener(this, this.multiDisp, ALK_EVENT_TYPE.DELETE);
    WizEventManager.removeListener(this, this.multiDisp, ALK_EVENT_TYPE.EDIT);
  }

  public get validable(): boolean {
    if(this.showNullScen) { // mode globalScen
      return this.scenDS.selectedItem?.code !== this.scenarioCode;
    } else {
      return !!this.scenDS.selectedItem && this.scenDS.selectedItem?.code !== this.scenarioCode;
    }
    
  }

  /** affichage du bouton de suppression du scenar */
  public get showNullScen(): boolean {
    return this.widgetConfig?.k === WIDGET_KIND.GLOBAL_FILTER;
  }

  /**
   * alimentation de la popUp
   */
  private feed(): void {
    this.mainFilter.configure(['code', 'trsLabel','parentCode', 'parentTrsLabel'], null, null);
    /** 
     * alimentation de la DS des scenarii
     */
    let scens: ScenarioVO[] =  this.model.scenarioEngine.getScenarioByVisionAndLevel(this.widgetConfig.v, this.widgetConfig.vs);
    scens.push( this.model.scenarioEngine.generateMain(this.widgetConfig.v));


    this.scenDS.setSource(scens);

    let selectSen:ScenarioVO; // alimentation de l'élément sélectionné
    // console.log('manageScen:', this.widgetConfig.scenCode);
    if(!!this.scenarioCode) {
     selectSen = this.scenDS.getItemByKey(this.scenarioCode); // ScenarioEngine.getScenario(this.widgetConfig.v, this.scenarioCode);
    } 
    if(!selectSen && this.widgetConfig.k !== WIDGET_KIND.GLOBAL_FILTER) { // toujours un scenar, sauf pour globalFilter
      selectSen = this.scenDS.getItemByKey(ViewData.DEFAULT_SCENARIO);
    }
    this.scenDS.select([selectSen]);
  }

  /** sur save, alimentation de l'objet de configuration */
  public save(): void {
    let scenCode: string = this.scenDS.selectedItem?.code;
    if(!scenCode) { // on évite le undefined
      scenCode = null;
    }
    this.widgetConfig.scenCode = scenCode;
    this.callback(this.widgetConfig);
    this.hide();
  }


  private selectScen(e: WizEvent): void {
    this.cdref.markForCheck();
  }
  
  public doAct(action: string): void {
    switch (action) {
      case 'create': // demande de create
        // console.log('add new Scen');
        this.addScen();
        break;
      case 'cancel': // pas de scenar sur filtre global
        this.scenDS.clearSelection();
        // console.log('cancel Scen');
        break;
    }
  }

  /**
     * popUp de configuration des scenarii
     */
  public addScen(): void {
    this.addOrEditPop(EM_ACTION.CREATE); 
  }

 /**
   * popUp de configuration des scenarii
   */
 public editScen(): void {
  this.addOrEditPop(EM_ACTION.UPDATE); 
}

  private addOrEditPop(mode: string): void {
    let data: addScenarioParam = {
      model: this.model,
      scenarii: this.scenDS.getSource(),
      parent: this.scenDS.selectedItem,
      callback: this.backFromAddScen.bind(this),
      mode: mode,
    }
    
    const pop: WizPopUp = new WizPopUp(
      ScenAddPop,
      true,
      false,
      WIZ_PHYSIC_POSITION.MIDDLE_CENTER,
      '600px',
      '400px',
      data
    );
    pop.show();
  }

  private backFromAddScen(): void {
    this.feed();
    // console.log('backFromAddScen');
  }

  /**
   * demande de suppression d'une scen value depuis le le tree
   * @param e evenement allkeyme
   */
  private askdeleteScen(e: AlkEvent): void {
    e.preventDefault(); // pas de sélection dans le tree
    // TODO TODO suppression directe si existe pas
    let cmd = new ScenarioStatsCmd( this.model).do(e.data, this.deleteScenCheck.bind(this));// chargement des stats
    cmd.execute();
    
    // this.deleteScen(e.data);
  }

  /**
   * contrôle avant suppression. Si présence de lignes ou de scenar enfant
   * @param call 
   */
  private deleteScenCheck(call: ScenarioStatsCmd): void {
    let scen: ScenarioVO = call.vo;

    // si scenarii enfants, warning
    if(!! this.model.scenarioEngine.getAllChildren(scen)) {
      // console.log('doDelete children', scen, ScenarioEngine.getAllChildren(scen));
      WizPopUp.confirm(
        '$.scenario.delete.$',
        '$.scenario.delete.haschild.$',
        this.doDelete.bind(this),
        CONFIRMATION_MODE.CONFIRM_CANCEL,
        scen
      ).show();
    } else if ( this.model.scenarioEngine.hasLines(scen)) {
      // console.log('doDelete haslines', scen, ScenarioEngine.hasLines(scen));
      WizPopUp.confirm(
        '$.scenario.delete.$',
        '$.scenario.delete.haslines.$',
        this.doDelete.bind(this),
        CONFIRMATION_MODE.CONFIRM_CANCEL,
        scen
      ).show();
    } else {
      let cro: ConfirmationResultObj = ConfirmationResultObj.build(CONFIRMATION_RESULT.CONFIRM, scen);
      this.doDelete(cro);
      // console.log('doDelete', scen);
    }
  }


  // suppression réelle
  private doDelete(result: ConfirmationResultObj): void {
    if (result.result === CONFIRMATION_RESULT.CONFIRM) {
      let scen: ScenarioVO = result.data;
      let toDieMap: Map<string, ScenarioVO> =  this.model.scenarioEngine.getAllChildren(scen, true);

      let cmd = new DeleteScenariiCommand( this.model).do(scen.visionCode, scen, [...toDieMap.values()], this.backFromDelete.bind(this));
      cmd.execute();
      
    }
  }

  private backFromDelete(cmd: DeleteScenariiCommand): void {
    // console.log('backFromDelete', cmd);
    WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.DELETE_SCENARIO, cmd.scenarii, this));
    this.feed();
  }




}
